import React, {useState} from "react"

import authentication from "@irati/auth"
import styled from "styled-components"
import momentTZ from "moment-timezone"
import Validation from "@irati/utils/validation"
import {H3} from "@irati/ui/src/Typography/Heading"
import tracking from "@irati-tracking/recognition/recognition"
import {FormattedMessage} from "@irati/utils/lang"
import RecognitionService from "@irati/recognition/services/Recognition"
import UserSearch from "@irati/ui/src/UserSearch"
import getId from "@irati/utils/id"
import {injectIntl, InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Form from "@irati/ui/src/Form"
import {CelebrationTag, GroupCelebration, User} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import mainTheme from "@irati/ui/src/global/theme"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {CreateGroupCelebrationInput} from "@irati/types/ops"
import {Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {useBudgetsList} from "@irati/wallet/hooks/budget"
import RewardRecognitionBanner, {RewardInfo} from "../RewardRecognition/RewardRecognitionBanner"
import Scheduler from "../CreateRecognition/Scheduler"
import CelebrationTags from "./CelebrationTags"

interface Props extends InjectIntlProps {
  onRecognitionChange: (celebration: CreateGroupCelebrationInput) => void
  onRecognitionCreated: (celebration: GroupCelebration, pointsPerUser?: number) => void
  scheduledGroupCelebration: CreateGroupCelebrationInput
}
const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${mainTheme.spacing.three};
  gap: ${mainTheme.spacing.three};
  textarea {
    resize: vertical;
    min-height: 40px;
  }

  .Select-control > *:last-child {
    padding-right: 10px;
  }
`

const getStartDate = () => {
  const currentTimezone = momentTZ.tz.guess()
  const now = momentTZ().tz(currentTimezone)
  now.set({
    minutes: 0,
    milliseconds: 0,
    seconds: 0,
    hours: 9,
    dayOfYear: now.dayOfYear() + 3
  })
  return {
    date: now,
    timezone: currentTimezone
  }
}
const CreateGroupCelebration: React.FC<Props> = (props) => {
  const {onRecognitionChange, onRecognitionCreated, formatMessage, scheduledGroupCelebration} = props

  const {data: budgetData} = useBudgetsList()
  const [rewardInfo, setRewardInfo] = useState<RewardInfo>({
    pointsPerUser: null,
    budgetId: null,
    useUnassigned: false
  })
  const [submit, setSubmit] = useState<Promise<any>>()
  const [receiver, setReceiver] = useState<User>()
  const [collaborators, setCollaborators] = useState<User[]>()
  const canReward = authentication.apps("wallet.flexiRewards.canRewardEmployees")
  const handleComplete = async (createGroupCelebration: GroupCelebration) => {
    onRecognitionCreated(createGroupCelebration, rewardInfo.pointsPerUser)
  }
  const handleRecognitionError = (response: any) => {
    tracking.errorsRecognition(response)
  }
  const handleCollaboratorsChange = (_, users: User[]) => {
    setCollaborators(users)
    onRecognitionChange({
      ...scheduledGroupCelebration,
      collaboratorIds: users.map(({uuid}) => uuid) || []
    })
  }
  const handleReceiverChange = (_, user: User) => {
    setReceiver(user)
    onRecognitionChange({
      ...scheduledGroupCelebration,
      receiverId: user.uuid
    })
  }
  const onRewardChange = (newRewardInfo: RewardInfo) => {
    const isUnassignedBudget = newRewardInfo?.budgetId === budgetData?.budgets?.unassigned.uuid
    setRewardInfo(newRewardInfo)
    onRecognitionChange({
      ...scheduledGroupCelebration,
      rewardAmount: newRewardInfo.pointsPerUser || 0,
      budget:
        isUnassignedBudget || !newRewardInfo.budgetId
          ? null
          : {
              categoryId: newRewardInfo.budgetId,
              useUnassigned: newRewardInfo.useUnassigned
            }
    })
  }
  const onScheduledTimeChange = (date: string, timezone: string) => {
    onRecognitionChange({
      ...scheduledGroupCelebration,
      scheduledAt: date,
      originalTimezone: timezone
    })
  }
  const handleSubmit = () => {
    const submitPromise = RecognitionService.createGroupCelebration({scheduledGroupCelebration})
      .then(handleComplete)
      .catch(handleRecognitionError)
      .finally(() => setSubmit())
    setSubmit(submitPromise)
  }
  const onCelebrationTagChange = (value: CelebrationTag) => {
    onRecognitionChange({
      ...scheduledGroupCelebration,
      greetingId: value.uuid
    })
  }
  return (
    <Spacing>
      <H3>
        <FormattedMessage id="recognition.popup.createGroupCelebration.create.header" />
      </H3>
      <StyledForm>
        <Body small>
          <FormattedMessage id="recognition.popup.createGroupCelebration.create.subtitle" />
        </Body>
        <CelebrationTags onChange={onCelebrationTagChange} />
        <UserSearch
          onChange={handleReceiverChange}
          removeSelf
          value={receiver}
          skipRecipients={scheduledGroupCelebration.collaboratorIds}
          autoFocus
          flush
          validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"}))}
          label="recognition.fields.groupCelebration.receiver.label"
          placeholder="recognition.fields.groupCelebration.receiver.placeholder"
        />
        <UserSearch
          onChange={handleCollaboratorsChange}
          removeSelf
          flush
          skipRecipients={receiver ? [receiver?.uuid] : []}
          value={collaborators}
          isMulti
          autoFocus
          validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"}))}
          label="recognition.fields.groupCelebration.collaborators.label"
          placeholder="recognition.fields.groupCelebration.collaborators.placeholder"
        />
        <Scheduler
          onChange={onScheduledTimeChange}
          defaultValue={getStartDate()}
          label="recognition.fields.groupCelebration.scheduler.label"
          forced
        />
        {canReward && (
          <RewardRecognitionBanner
            defaultSettings={rewardInfo}
            totRecipients={1}
            onChange={onRewardChange}
            title="recognition.reward.rewardGroupCelebration.title"
          />
        )}
      </StyledForm>
      <Flex justifyContent="flex-end">
        <ValidationButton load={submit} primary id={getId("submitButton", props)} onClick={handleSubmit}>
          <FormattedMessage id="recognition.create.scheduleGroupCelebration.cta" />
        </ValidationButton>
      </Flex>
    </Spacing>
  )
}
export default injectIntl(CreateGroupCelebration)
